import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import 'chartjs-adapter-moment';
import { Chart as ChartJS } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import AuthContext from "../../../contexts/auth-context";

import { ChartHeader, ZoomControls, applyChartTheme, CHART_THEME } from "../../Concession/charts/chartTheme";
ChartJS.register(zoomPlugin, annotationPlugin);

// Function to convert game start time to annotation timestamp matching the chart's time system
const convertGameStartTimeToAnnotation = (gameDate, gameStartTime, baseDate) => {
  if (!gameDate || !gameStartTime || !baseDate) return null;
  
  const [hours, minutes] = gameStartTime.split(':').map(Number);
  
  // Create base date at midnight (same as chart's time conversion)
  const chartBaseDate = new Date(baseDate);
  chartBaseDate.setHours(0, 0, 0, 0);
  
  // Create game start time using the same logic as the chart
  const gameStartTime24 = hours * 60 + minutes; // Convert to minutes from midnight
  const annotationTime = new Date(chartBaseDate);
  annotationTime.setHours(0, gameStartTime24, 0, 0);
  
  return annotationTime.toISOString();
};

const RawChart = ({ data, currentLocation, dayType, currentGame }) => {
  const authCtx = useContext(AuthContext);
  const [dataValues, setDataValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);

  useEffect(() => {
    setLoading(true);

    const updatedData = data
      .filter((entry) => entry.time > 600)
      .map((entry) => {
        const dateTime = new Date(data[0].time2);
        dateTime.setHours(0, entry.time, 0, 0);
        return {
          ...entry,
          time: dateTime,
        };
      });

    setDataValues(updatedData);
    setLoading(false);
  }, [data]);

  const maxValue = Math.max(...dataValues.map((item) => item.total));

  // Create game start annotation with useMemo - only for game days
  const gameStartAnnotation = useMemo(() => {
    if (dayType !== 'game') return null;
    return currentGame?.date && currentGame?.gameStartTime && data.length > 0 ? 
      convertGameStartTimeToAnnotation(currentGame.date, currentGame.gameStartTime, data[0].time2) : null;
  }, [dayType, currentGame?.date, currentGame?.gameStartTime, data]);

  var barWidth = 3;

  const chartData = {
    labels: dataValues.map((item) => item.time),
    datasets: [
      {
        label: "Raw Count",
        data: dataValues.map((item) => item.total),
        backgroundColor: dataValues.map((item) => (item.total >= currentLocation.maxOccupancy ? "#d97706" : "#3b82f6")),
        barThickness: barWidth,
        borderRadius: 2.5,
      },
    ],
  };

  // Gets primary text color for chart
  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt').trim();

  // Memoize chart options to prevent unnecessary recreations
  const mainChartOptions = useMemo(() => ({
    scales: {
      x: {
        title: {
          color: primaryTextColor,
          display: true,
          text: "Time",
          padding: { top: 0, bottom: 10 },
        },
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "h A",
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          color: primaryTextColor,
          source: "auto",
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        display: true,
        title: {
          color: primaryTextColor,
          display: true,
          text: "People",
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: false,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          beginAtZero: true,
          callback: function (value) {
            if (value % 1 === 0) {
              return value;
            }
          },
        },
        max: maxValue,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'rectRounded',
          generateLabels: function(chart) {
            // Get default labels for the datasets
            const defaultLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            
            // Add game start indicator if we have one - put it FIRST
            if (gameStartAnnotation) {
              const gameStartLabel = {
                text: 'Game Start',//authCtx.league === "mlb" ? "First Pitch" : authCtx.league === "nfl" ? "Kick Off" : "Game Start",
                fillStyle: 'transparent',
                strokeStyle: CHART_THEME.gameStart,
                lineWidth: 3,
                lineDash: [8, 2],
                pointStyle: 'line',
                hidden: false,
                index: -1 // Special index to identify this as non-clickable
              };
              
              // Put game start at the beginning
              return [gameStartLabel, ...defaultLabels];
            }
            
            return defaultLabels;
          }
        },
        onClick: function(evt, legendItem, legend) {
          // Only handle clicks for dataset items (not game start)
          if (legendItem.index !== -1) {
            // Use the default legend click behavior for dataset items
            ChartJS.defaults.plugins.legend.onClick.call(this, evt, legendItem, legend);
          }
          // Do nothing for game start (index === -1)
        }
      },
      zoom: {
        limits: {
          x: { min: 'original', max: 'original', minRange: 60 * 60 * 1000 },
        },
        zoom: {
          wheel: {
            enabled: false,
          },
          pinch: {
            enabled: false
          },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
      },
      annotation: {
        annotations: gameStartAnnotation ? [
          {
            type: 'line',
            mode: 'vertical',
            scaleID: 'x',
            value: gameStartAnnotation,
            borderColor: CHART_THEME.gameStart,
            borderWidth: 2,
            borderDash: [8, 4]
          }
        ] : []
      },
    },
  }), [gameStartAnnotation, maxValue, primaryTextColor, authCtx.league]);

  const handleResetZoom = () => {
    if (chartRef.current) {
      barWidth = 3;
      chartRef.current.resetZoom();
    }
  };

  const handleZoomIn = () => {
    if (chartRef.current && barWidth < 8) {
      barWidth += 1;
      chartRef.current.zoom(1.25);
    }
  };

  const handleZoomOut = () => {
    if (chartRef.current && barWidth > 0) {
      barWidth -= 1;
      chartRef.current.zoom(0.75);
    }
  };

  if (!loading) {
    return (
      <div className="graph panel-2 cx-chart-card">
        <ChartHeader
          title="Raw occupancy"
          subtitle="People in the store over the day. Amber bars are at or over capacity."
          controls={<ZoomControls chartRef={chartRef} onIn={handleZoomIn} onOut={handleZoomOut} onReset={handleResetZoom} />}
        />
        <div style={{ overflowX: "auto", marginTop: "8px" }}>
          <div style={{ height: "300px", minWidth: "600px" }}>
            <Bar 
              key={dayType}
              ref={chartRef} 
              data={chartData} 
              options={applyChartTheme(mainChartOptions)} 
            />
          </div>
        </div>
      </div>
    );
  };
}

export default RawChart;